import React, { useState, useEffect, useMemo, useCallback } from 'react';

const API = 'https://api.glowytics.com';
const TR_MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const fmtInt = (n) => Number(n || 0).toLocaleString('tr-TR');

// Mirrors the backend: a zero baseline has no percentage to report, so it reads "yeni"
// rather than a fabricated +100%.
const pctChange = (prev, curr) => {
  const p = Number(prev || 0);
  const c = Number(curr || 0);
  if (p === 0) return c === 0 ? 0 : null;
  return ((c - p) / p) * 100;
};
const fmtPct = (v) => (v === null ? 'yeni' : `${v > 0 ? '+' : ''}${v.toFixed(1).replace('.', ',')}%`);

// currentBrandName is not always set; fall back to the selectedBrand JSON.
const getBrandName = () => {
  const direct = localStorage.getItem('currentBrandName');
  if (direct) return direct;
  try {
    const stored = localStorage.getItem('selectedBrand');
    if (stored) return JSON.parse(stored)?.name || '';
  } catch { /* ignore */ }
  return '';
};

// Google publishes a month's volume with a lag, so the newest complete month is
// usually the previous one — default there instead of the current month.
const defaultTarget = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

export default function SearchVolumeDigest() {
  const [target, setTarget] = useState(defaultTarget);
  const [keywords, setKeywords] = useState([]);
  const [selected, setSelected] = useState({});
  const [volumes, setVolumes] = useState({});     // keyword -> monthly[]
  const [loadingKw, setLoadingKw] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const [subject, setSubject] = useState('');
  const [intro, setIntro] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [recipients, setRecipients] = useState('');
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);

  const brandId = localStorage.getItem('currentBrandId');
  const brandName = getBrandName();

  const monthLabel = `${TR_MONTHS[target.month - 1]} ${target.year}`;
  const prev = useMemo(() => {
    const d = new Date(target.year, target.month - 1, 1);
    d.setMonth(d.getMonth() - 1);
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  }, [target]);
  const prevMonthLabel = `${TR_MONTHS[prev.month - 1]} ${prev.year}`;

  const valueAt = useCallback((kw, y, m) => {
    const arr = volumes[kw];
    if (!Array.isArray(arr)) return null;
    const row = arr.find(r => Number(r.year) === y && Number(r.month) === m);
    return row ? Number(row.monthly_searches || 0) : null;
  }, [volumes]);

  // Saved keywords for the active brand (same table the Search Volume page writes to).
  useEffect(() => {
    (async () => {
      try {
        setLoadingKw(true);
        const res = await fetch(`${API}/api/search-volume/keywords`, {
          credentials: 'include',
          headers: { ...(brandId ? { 'X-Brand-ID': brandId } : {}) }
        });
        if (!res.ok) { setKeywords([]); return; }
        const data = await res.json();
        const list = (Array.isArray(data.keywords) ? data.keywords : []).map(k => k.keyword).filter(Boolean);
        setKeywords(list);
        setSelected(Object.fromEntries(list.map(k => [k, true])));
      } catch { setKeywords([]); }
      finally { setLoadingKw(false); }
    })();
  }, [brandId]);

  const activeKeywords = useMemo(() => keywords.filter(k => selected[k]), [keywords, selected]);

  const fetchVolumes = async () => {
    const list = activeKeywords;
    if (!list.length) return;
    setFetching(true);
    setError(null);
    setProgress({ done: 0, total: list.length });
    const failed = [];
    for (let i = 0; i < list.length; i++) {
      const kw = list[i];
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await fetch(`${API}/api/search-volume/monthly`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) },
          body: JSON.stringify({ keyword: kw, months: 0 })
        });
        if (!res.ok) throw new Error('istek başarısız');
        // eslint-disable-next-line no-await-in-loop
        const data = await res.json();
        setVolumes(prevV => ({ ...prevV, [kw]: Array.isArray(data.monthly) ? data.monthly : [] }));
      } catch {
        failed.push(kw);
        setVolumes(prevV => ({ ...prevV, [kw]: [] }));
      }
      setProgress({ done: i + 1, total: list.length });
    }
    if (failed.length) setError(`Şu kelimeler çekilemedi: ${failed.join(', ')}`);
    setFetching(false);
  };

  // Only keywords with real data for both months can carry a change figure.
  const rows = useMemo(() => activeKeywords.map(kw => {
    const p = valueAt(kw, prev.year, prev.month);
    const c = valueAt(kw, target.year, target.month);
    return { keyword: kw, prev: p, curr: c, change: (p === null || c === null) ? undefined : pctChange(p, c) };
  }), [activeKeywords, valueAt, prev, target]);

  const readyRows = useMemo(() => rows.filter(r => r.prev !== null && r.curr !== null), [rows]);
  const missingRows = useMemo(() => rows.filter(r => r.prev === null || r.curr === null), [rows]);
  const hasData = readyRows.length > 0;

  const payload = () => ({
    monthLabel, prevMonthLabel, brandName,
    rows: readyRows.map(r => ({ keyword: r.keyword, prev: r.prev, curr: r.curr }))
  });

  const doDraft = async () => {
    try {
      setDrafting(true); setError(null);
      const res = await fetch(`${API}/api/search-volume-digest/draft`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload())
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.details || data.error || 'Taslak üretilemedi');
      setSubject(data.subject || '');
      setIntro(data.intro || '');
    } catch (e) { setError(e.message); }
    finally { setDrafting(false); }
  };

  // Preview comes from the server so what is shown is byte-for-byte what gets sent.
  useEffect(() => {
    if (!intro || !hasData) { setPreviewHtml(''); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/search-volume-digest/preview`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload(), intro })
        });
        const data = await res.json();
        if (!cancelled && res.ok) setPreviewHtml(data.html || '');
      } catch { /* preview is best-effort */ }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intro, hasData, readyRows, monthLabel, prevMonthLabel]);

  const doSend = async () => {
    const to = recipients.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
    try {
      setSending(true); setError(null);
      const res = await fetch(`${API}/api/search-volume-digest/send`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload(), subject, intro, to })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.details || data.error || 'Gönderilemedi');
      setToast(`${data.sent} e-posta gönderildi${data.failed ? `, ${data.failed} başarısız` : ''}.`);
      setConfirmSend(false);
      setTimeout(() => setToast(null), 6000);
    } catch (e) { setError(e.message); setConfirmSend(false); }
    finally { setSending(false); }
  };

  const recipientList = recipients.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
  const canSend = hasData && subject.trim() && intro.trim() && recipientList.length > 0;

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2022; y--) years.push(y);

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-[1400px] mx-auto space-y-5">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">Aranma Hacmi Bülteni</h1>
              <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[11px] font-bold tracking-wide uppercase">Internal</span>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {monthLabel} verilerini çek, bir önceki aya göre değişimi gör, Gemini'ye kısa bir mail yazdır, müşteriye gönder.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={target.month}
              onChange={(e) => setTarget(t => ({ ...t, month: Number(e.target.value) }))}
              className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
            >
              {TR_MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select
              value={target.year}
              onChange={(e) => setTarget(t => ({ ...t, year: Number(e.target.value) }))}
              className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
            >
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button
              onClick={fetchVolumes}
              disabled={fetching || !activeKeywords.length}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
            >
              {fetching ? `Çekiliyor ${progress.done}/${progress.total}` : 'Verileri çek'}
            </button>
          </div>
        </div>

        {error && (
          <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">{error}</div>
        )}
        {toast && (
          <div className="px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800 font-medium">{toast}</div>
        )}

        {/* Keyword chips */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Kelimeler {brandName && <span className="text-slate-400 normal-case font-normal">· {brandName}</span>}
          </div>
          {loadingKw ? (
            <div className="text-sm text-slate-400">Yükleniyor…</div>
          ) : !keywords.length ? (
            <div className="text-sm text-slate-500">
              Bu marka için kayıtlı kelime yok. Search Volume sayfasından kelime ekleyebilirsin.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {keywords.map(k => (
                <button
                  key={k}
                  onClick={() => setSelected(s => ({ ...s, [k]: !s[k] }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                    selected[k] ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                  }`}
                >{k}</button>
              ))}
            </div>
          )}
        </div>

        {hasData && (
          <>
            {/* Change cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {readyRows.map(r => {
                const up = r.change !== null && r.change > 0.05;
                const down = r.change !== null && r.change < -0.05;
                const tone = up ? 'text-emerald-600' : down ? 'text-red-600' : 'text-slate-500';
                const bg = up ? 'bg-emerald-50 border-emerald-200' : down ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200';
                return (
                  <div key={r.keyword} className={`rounded-xl border p-4 ${bg}`}>
                    <div className="text-xs font-semibold text-slate-500 truncate" title={r.keyword}>{r.keyword}</div>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="text-2xl font-extrabold text-slate-900 tabular-nums">{fmtInt(r.curr)}</span>
                      <span className={`text-sm font-bold ${tone}`}>
                        {up ? '▲' : down ? '▼' : '•'} {fmtPct(r.change)}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500 tabular-nums">
                      {prevMonthLabel}: {fmtInt(r.prev)}
                    </div>
                  </div>
                );
              })}
            </div>

            {missingRows.length > 0 && (
              <div className="px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
                Şu kelimeler için {monthLabel} veya {prevMonthLabel} verisi yok, maile dahil edilmeyecek:{' '}
                <b>{missingRows.map(r => r.keyword).join(', ')}</b>
              </div>
            )}

            {/* Compose + preview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Mail metni</h2>
                  <button
                    onClick={doDraft}
                    disabled={drafting}
                    className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50"
                  >{drafting ? 'Yazıyor…' : intro ? 'Yeniden yaz' : 'Gemini ile yaz'}</button>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Konu</label>
                  <input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Gemini ile yaz'a bas veya kendin gir"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Giriş metni</label>
                  <textarea
                    value={intro}
                    onChange={(e) => setIntro(e.target.value)}
                    rows={6}
                    placeholder="Tabloyu mail kendisi ekliyor — buraya sadece giriş yazısı."
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm resize-y"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Alıcılar <span className="font-normal text-slate-400">· virgül veya boşlukla ayır</span>
                  </label>
                  <input
                    value={recipients}
                    onChange={(e) => setRecipients(e.target.value)}
                    placeholder="musteri@marka.com, ekip@marka.com"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  />
                </div>

                <button
                  onClick={() => setConfirmSend(true)}
                  disabled={!canSend || sending}
                  className="w-full px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-40"
                >
                  {recipientList.length ? `${recipientList.length} kişiye gönder` : 'Gönder'}
                </button>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                  <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Önizleme</h2>
                  <span className="text-[11px] text-slate-400">müşteriye birebir bu gidecek</span>
                </div>
                {previewHtml ? (
                  <iframe title="preview" srcDoc={previewHtml} sandbox="" className="w-full h-[620px] border-0" />
                ) : (
                  <div className="h-[620px] flex items-center justify-center text-sm text-slate-400 px-8 text-center">
                    Metni yazdır, önizleme burada belirecek.
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {!hasData && !fetching && activeKeywords.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <p className="text-sm text-slate-500">
              "Verileri çek" ile {monthLabel} ve {prevMonthLabel} hacimlerini Google Ads'ten getir.
            </p>
          </div>
        )}
      </div>

      {/* Send confirmation — this leaves the building, so never one-click. */}
      {confirmSend && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmSend(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-bold text-slate-900">Müşteriye gönderilsin mi?</h3>
            <p className="text-sm text-slate-600 mt-2">
              <b>{recipientList.length}</b> alıcıya <b>{readyRows.length}</b> kelimelik {monthLabel} bülteni gidecek.
            </p>
            <div className="mt-3 max-h-32 overflow-auto rounded-lg bg-slate-50 border border-slate-200 p-3">
              {recipientList.map(r => <div key={r} className="text-xs text-slate-700 font-mono">{r}</div>)}
            </div>
            <p className="text-xs text-slate-500 mt-3">Konu: <b className="text-slate-700">{subject}</b></p>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={() => setConfirmSend(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium">Vazgeç</button>
              <button onClick={doSend} disabled={sending} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold disabled:opacity-50">
                {sending ? 'Gönderiliyor…' : 'Evet, gönder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
